import { motion } from "framer-motion";
import { ShieldCheck, Droplets, Sun, Sparkles, Download, ArrowRight, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const benefits = [
    { icon: ShieldCheck, text: "Seals paintwork against acid rain, tree gum and bird droppings" },
    { icon: Sun, text: "Stops UV and sun damage — no more dull, weathered, oxidised panels" },
    { icon: Droplets, text: "Severely hydrophobic — water and dirt slide straight off" },
    { icon: Sparkles, text: "Glass-like gloss that makes washing effortless for years" },
];

const tiers = [
    { size: "Up to 21 ft (6.31 m)", price: "£1,150.00" },
    { size: "21–27 ft (6.31–8.23 m)", price: "£1,190.00" },
    { size: "27–30 ft (8.23–9.14 m)", price: "£1,290.00" },
];

const reveal = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const CeramicSection = () => {
    return (
        <section id="ceramic" className="relative py-24 sm:py-32 bg-indigo-950 overflow-hidden pv-noise" data-testid="ceramic-section">
            <div className="absolute inset-0 pv-stars opacity-30 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
                <motion.div {...reveal} className="inline-block bg-white p-4 mb-12 shadow-[0_4px_16px_rgba(0,0,0,0.35)]" data-testid="ceramic-kisho-logo">
                    <img
                        src="/assets/kisho-logo.png"
                        alt="KISHO Ceramic Coatings logo"
                        className="w-24 sm:w-32 h-auto"
                    />
                </motion.div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
                    <div className="lg:col-span-7">
                        <motion.p {...reveal} className="text-xs tracking-[0.3em] uppercase font-semibold text-blue-300 mb-6" data-testid="ceramic-eyebrow">
                            Featured treatment · KISHO Japan
                        </motion.p>
                        <motion.h2 {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-medium text-white mb-8" data-testid="ceramic-heading">
                            Ceramic coating, guaranteed for{" "}
                            <span className="italic bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">five years.</span>
                        </motion.h2>
                        <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.2 }} className="text-base sm:text-lg leading-relaxed font-normal text-white/90 max-w-xl mb-10" data-testid="ceramic-copy">
                            KISHO's state-of-the-art nano coating builds thousands of microscopic ceramic
                            layers over your motorhome's paintwork — completely sealing it in. Even if
                            scratched, only a few layers are lifted, and the coating smooths itself back
                            to a perfect finish.
                        </motion.p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12" data-testid="ceramic-benefits">
                            {benefits.map((b, i) => (
                                <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: 0.25 + i * 0.08 }} className="flex items-start gap-3">
                                    <b.icon className="w-5 h-5 text-blue-300 shrink-0 mt-0.5" />
                                    <p className="text-sm sm:text-base font-medium text-white leading-relaxed">{b.text}</p>
                                </motion.div>
                            ))}
                        </div>
                        <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.5 }} className="flex flex-wrap items-center gap-4">
                            <Dialog>
                                <DialogTrigger asChild>
                                    <motion.button
                                        whileTap={{ scale: 0.97 }}
                                        className="inline-flex items-center gap-3 bg-white hover:bg-blue-50 text-indigo-950 text-sm tracking-[0.12em] uppercase font-bold px-8 py-4 transition-colors duration-300"
                                        data-testid="ceramic-flyer-view-button"
                                    >
                                        <Eye className="w-4 h-4" /> View the full information
                                    </motion.button>
                                </DialogTrigger>
                                <DialogContent className="max-w-3xl w-[92vw] max-h-[90vh] overflow-y-auto bg-white border-indigo-100" data-testid="ceramic-flyer-dialog">
                                    <DialogHeader>
                                        <DialogTitle className="font-serif-display text-2xl tracking-tight text-indigo-950">KISHO Ceramic Coatings</DialogTitle>
                                    </DialogHeader>
                                    <img src="/assets/flyer-page-1.webp" alt="KISHO ceramic coating flyer — front" className="w-full border border-indigo-100" data-testid="flyer-page-front" />
                                    <img src="/assets/flyer-page-2.webp" alt="KISHO ceramic coating flyer — prices and interior protection" className="w-full border border-indigo-100" data-testid="flyer-page-reverse" />
                                    <a
                                        href="/assets/kisho-flyer.pdf"
                                        download="Planet-Valet-KISHO-Ceramic-Coating.pdf"
                                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-purple-600 hover:text-purple-500 transition-colors"
                                        data-testid="ceramic-flyer-download-link"
                                    >
                                        <Download className="w-4 h-4" /> Download as PDF
                                    </a>
                                </DialogContent>
                            </Dialog>
                            <motion.button
                                whileTap={{ scale: 0.97 }}
                                onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
                                className="inline-flex items-center gap-3 border border-indigo-400/40 hover:border-blue-300 hover:text-blue-300 text-white text-sm tracking-[0.12em] uppercase font-semibold px-8 py-4 transition-colors duration-300"
                                data-testid="ceramic-book-button"
                            >
                                Book ceramic coating <ArrowRight className="w-4 h-4" />
                            </motion.button>
                        </motion.div>
                    </div>

                    <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.3 }} className="lg:col-span-5">
                        <div className="border border-indigo-400/25 bg-indigo-900/40 p-8 sm:p-10" data-testid="ceramic-pricing-panel">
                            <div className="flex items-center gap-4 mb-8 pb-8 border-b border-indigo-400/20">
                                <span className="font-serif-display text-6xl sm:text-7xl font-light text-blue-300" data-testid="ceramic-guarantee-years">5</span>
                                <div>
                                    <p className="text-xs tracking-[0.25em] uppercase font-bold text-white">Year guarantee</p>
                                    <p className="text-sm font-normal text-blue-200 mt-1">On every KISHO application</p>
                                </div>
                            </div>
                            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-blue-200 mb-5">Priced by vehicle length</p>
                            <div className="space-y-0" data-testid="ceramic-price-tiers">
                                {tiers.map((t, i) => (
                                    <div key={i} className="flex items-center justify-between py-4 border-b border-indigo-400/15 last:border-b-0" data-testid={`ceramic-tier-${i}`}>
                                        <span className="text-sm sm:text-base font-normal text-white">{t.size}</span>
                                        <span className="font-serif-display text-xl sm:text-2xl text-white">{t.price}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-6 text-xs font-normal text-blue-200 leading-relaxed">
                                Includes full preparation and application. Final quote always confirmed before work begins.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
